import { useCallback, useEffect } from 'react';
import { flushSync } from 'react-dom';

import { useCanvasSelectionHandler } from './useCanvasSelectionHandler';

import { useArrowSideItemInfoModel } from '~/UI/components/molecules/SideItemOption/hooks/useArrowSideItemInfoModel';
import useSideItems from '~/UI/components/molecules/SideItemOption/hooks/useSideItems';
import { useCornerRadii } from '~/api/easy-estimator-api/corner-radii-api/hooks/useCornerRadii';
import { useSelectedEdge } from '~/api/easy-estimator-api/edges-api/hooks/useSelectedEdge';
import {
  BUMPOUT_CLOCKWISE_REMAINDER,
  BUMPOUT_COUNTERCLOCKWISE_REMAINDER,
  BUMPOUT_LENGTH,
  DEFAULT_CORNER_RADIUS,
} from '~/core/constants';
import { drawingTool } from '~/core/engine/DrawingTool';
import { EditableCornerLabelHandler } from '~/core/engine/EditableLabel';
import {
  CustomSideType,
  DiagonalCornerOptions,
  SideItemOption as SideItemOptionEnum,
  UserDesignOperation,
} from '~/core/enums';
import {
  roundToArrowLengthGranularity,
  updateShapeByEdge,
} from '~/core/helpers';
import { useRegisterUserAction } from '~/core/hooks/useRegisterUserAction';
import { useSelectedColor } from '~/core/hooks/useSelectedColor';
import { ArrowInfo, ColorModel, DrawingShape } from '~/core/models';
import {
  cloneCornerInfo,
  updateEdgeBumpOutDepth,
  updateEdgeRadiusSagitta,
} from '~/core/utils';
import {
  useApplianceContext,
  useDrawingShapeContext,
  useSlabImgContext,
} from '~/legacy/easy-estimator-page/context';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import {
  selectNeedRedrawCanvas,
  setPreventRepricing,
  updateNeedRedrawCanvas,
} from '~/redux/slices/drawingShape';
import { selectFabricCanvas } from '~/redux/slices/fabric';

export const useCanvasUpdateHandler = () => {
  const dispatch = useAppDispatch();
  const canvas = useAppSelector(selectFabricCanvas);
  const { selectedValue: selectedColor } = useSelectedColor();
  const needRedrawCanvas = useAppSelector(selectNeedRedrawCanvas);

  const { cornerRadii } = useCornerRadii();
  const slabImg = useSlabImgContext();
  const { removeDiagonalCornerOnShape, setCornerRadiusSize } =
    useCanvasSelectionHandler();
  const { isDraggingAppliance } = useApplianceContext();
  const { updateHeight } = useSideItems();
  const { getArrowWaterfall, getArrowWallSide } = useArrowSideItemInfoModel();
  const {
    getters: {
      drawingShapes,
      activeDrawingApplianceId,
      activeDrawingShape,
      showNotification,
    },
    handlers: { updateDrawingShapes, updateShowNotification },
  } = useDrawingShapeContext();
  const { selectedValue: selectedEdge } = useSelectedEdge();
  const { registerCustomSideAction, registerCornerAction, registerUserAction } =
    useRegisterUserAction();

  const {
    handlers: { setIsCornerRadiusConstraintModalOpen },
  } = useDrawingShapeContext();

  const updateDiagonalCornerDimension = useCallback(
    (arrow: ArrowInfo, shape: DrawingShape) => {
      Object.values(shape.cornerMap)
        .filter(
          (corner) => corner.diagonalLength && corner.id === arrow.cornerId
        )
        .forEach((cornerInfo) => {
          if (arrow.size === 0) {
            cornerInfo.label = DiagonalCornerOptions.REMOVE_DIAGONAL_CORNER;
            cornerInfo.productId = undefined;
            removeDiagonalCornerOnShape(cornerInfo);
          } else {
            if (
              arrow.maximumLength &&
              (arrow.size ?? 0) > arrow.maximumLength
            ) {
              arrow.size = arrow.maximumLength;
            }

            cornerInfo.diagonalLength = arrow.size;
          }
        });
    },
    [removeDiagonalCornerOnShape]
  );

  const updateWaterfallHeight = useCallback(
    (arrow: ArrowInfo) => {
      const sideItemWaterfall = getArrowWaterfall(arrow);

      return sideItemWaterfall && updateHeight(sideItemWaterfall);
    },
    [getArrowWaterfall, updateHeight]
  );

  const updateBacksplashHeight = useCallback(
    (arrow: ArrowInfo) => {
      const sideItemWallSide = getArrowWallSide(arrow);

      return sideItemWallSide && updateHeight(sideItemWallSide);
    },
    [getArrowWallSide, updateHeight]
  );

  const updateSideItem = useCallback(
    (args: {
      arrow: ArrowInfo;
      shape: DrawingShape;
      enteredMeasurementValue?: number;
    }) => {
      const { arrow, shape, enteredMeasurementValue } = args;
      const sideItemType = shape.drawingEdges.find(
        (edge) => edge.id === arrow.drawingEdgeId
      )?.sideItem?.type;

      if (
        enteredMeasurementValue !== undefined &&
        sideItemType === SideItemOptionEnum.WATERFALL
      ) {
        updateWaterfallHeight?.(arrow)?.(+enteredMeasurementValue);
      } else if (
        enteredMeasurementValue !== undefined &&
        sideItemType === SideItemOptionEnum.BACKSPLASH
      ) {
        updateBacksplashHeight?.(arrow)?.(+enteredMeasurementValue);
      }
    },
    [updateBacksplashHeight, updateWaterfallHeight]
  );

  const removeArrowEdge = useCallback(
    (args: { arrow: ArrowInfo; shape: DrawingShape }) => {
      const { arrow, shape } = args;
      // remove custom side
      const [edge] = shape.drawingEdges.filter(
        (edge) => edge.id === arrow.drawingEdgeId
      );

      edge.customSideType = undefined;
      edge.realRadius = undefined;
      if (edge.bumpOut) {
        edge.bumpOut.length = 0;
        edge.bumpOut.clockwiseRemainderLength = 0;
        edge.bumpOut.counterClockwiseRemainderLength = 0;
      }
    },
    []
  );

  const onDimensionEdit = useCallback(
    (
      arrow: ArrowInfo,
      shape: DrawingShape,
      enteredMeasurementValue?: number
    ) => {
      if (shape.id in showNotification) {
        updateShowNotification({ ...showNotification, [shape.id]: false });
      }

      const hasValidSize = (arrow?.size ?? 0) > 0;

      if (hasValidSize) {
        if (arrow.isRadiusOut) {
          updateEdgeRadiusSagitta(arrow, shape);
        } else if (arrow.isBumpOut) {
          updateEdgeBumpOutDepth(arrow, shape);
        } else if (arrow.isSideItemPresent && arrow.size) {
          updateSideItem({
            arrow,
            shape,
            enteredMeasurementValue,
          });
        } else if (arrow.isDiagonalCorner && arrow.size !== undefined) {
          updateDiagonalCornerDimension(arrow, shape);
        }
      } else if (arrow.drawingEdgeId && arrow?.size === 0) {
        removeArrowEdge({ shape, arrow });
      }

      if (arrow && !arrow.isSideItemPresent && hasValidSize) {
        updateShapeByEdge(arrow, shape, false);
        registerUserAction(UserDesignOperation.MODIFY_SHAPE_SIZE);
      }
      updateDrawingShapes([...drawingShapes]);
      dispatch(updateNeedRedrawCanvas(true));
    },
    [
      showNotification,
      updateDrawingShapes,
      drawingShapes,
      dispatch,
      updateShowNotification,
      updateSideItem,
      updateDiagonalCornerDimension,
      removeArrowEdge,
      registerUserAction,
    ]
  );

  const onBumpOutDimEdit = useCallback(
    (editedArrow: ArrowInfo, shape: DrawingShape) => {
      const bumpOutEdgeIdParts = editedArrow.drawingEdgeId?.split('-');

      if (bumpOutEdgeIdParts) {
        const parentEdgeId = bumpOutEdgeIdParts[0];
        const bumpOutArrowType = bumpOutEdgeIdParts[1];
        const newSize = editedArrow.size
          ? roundToArrowLengthGranularity(editedArrow.size)
          : 0;

        shape.drawingEdges
          .filter((edge) => edge.id === parentEdgeId)
          .forEach((edge) => {
            let change;

            if (edge.bumpOut) {
              switch (bumpOutArrowType) {
                case BUMPOUT_CLOCKWISE_REMAINDER.toString():
                  change = newSize - edge.bumpOut.clockwiseRemainderLength;
                  edge.bumpOut.clockwiseRemainderLength = newSize;
                  edge.bumpOut.counterClockwiseRemainderLength -= change;
                  break;
                case BUMPOUT_LENGTH.toString():
                  change = newSize - edge.bumpOut.length;
                  edge.bumpOut.length = newSize;
                  edge.bumpOut.counterClockwiseRemainderLength -= change / 2;
                  edge.bumpOut.clockwiseRemainderLength -= change / 2;
                  break;
                case BUMPOUT_COUNTERCLOCKWISE_REMAINDER.toString():
                  change =
                    newSize - edge.bumpOut.counterClockwiseRemainderLength;
                  edge.bumpOut.counterClockwiseRemainderLength = newSize;
                  edge.bumpOut.clockwiseRemainderLength -= change;
                  break;
              }
            }
          });
      }

      dispatch(setPreventRepricing(true));
      updateDrawingShapes([...drawingShapes]);
      dispatch(updateNeedRedrawCanvas(true));
      registerCustomSideAction(CustomSideType.BUMP_OUT);
    },
    [dispatch, drawingShapes, updateDrawingShapes, registerCustomSideAction]
  );

  const onCornerDimensionEdit: EditableCornerLabelHandler = useCallback(
    (originalCorner, shape, size) => {
      const editedCorner = cloneCornerInfo(originalCorner);

      for (const key in shape.cornerMap) {
        if (shape.cornerMap[key].id === editedCorner.id) {
          setCornerRadiusSize(editedCorner, size ?? DEFAULT_CORNER_RADIUS);
          shape.cornerMap[key] = editedCorner;
        }
      }

      updateDrawingShapes([...drawingShapes]);
      dispatch(updateNeedRedrawCanvas(true));
      registerCornerAction('cornerRadius');
    },
    [
      dispatch,
      setCornerRadiusSize,
      updateDrawingShapes,
      drawingShapes,
      registerCornerAction,
    ]
  );

  const updateShapesDimensions = useCallback(
    (args?: { color?: ColorModel; drawingShapes?: DrawingShape[] }) => {
      const {
        color = selectedColor,
        drawingShapes: drawingShapesToMutate = drawingShapes,
      } = args ?? {};

      if (!color) {
        return;
      }

      const { wereCornersConstrained } = drawingTool.addDrawingShape({
        canvas,
        shapes: drawingShapesToMutate,
        onEdit: onBumpOutDimEdit,
        useOverlay: isDraggingAppliance,
        activeDrawingShape,
        activeAppliance: activeDrawingApplianceId,
        cornerRadii,
      });

      if (wereCornersConstrained) {
        setIsCornerRadiusConstraintModalOpen(true);
      }

      drawingTool.addMeasurements(
        canvas,
        drawingShapesToMutate,
        color.overlayColor,
        onDimensionEdit,
        activeDrawingShape
      );

      drawingTool.addCornerRadiiMeasurements(
        canvas,
        drawingShapesToMutate,
        color.overlayColor,
        onCornerDimensionEdit
      );
    },
    [
      selectedColor,
      drawingShapes,
      canvas,
      onBumpOutDimEdit,
      isDraggingAppliance,
      activeDrawingShape,
      activeDrawingApplianceId,
      cornerRadii,
      onDimensionEdit,
      onCornerDimensionEdit,
      setIsCornerRadiusConstraintModalOpen,
    ]
  );

  const updateShapesTexts = useCallback(
    (overlayColor: string, shapes: DrawingShape[]) => {
      const shapeRenameListener = (shape: DrawingShape) =>
        shape?.onRename?.subscribe((text) => {
          // Use flushSync to ensure state update is processed synchronously
          // before dispatching Redux action that triggers canvas redraw.
          // This fixes a race condition where Redux update was faster than
          // Context state update, causing canvas to redraw with stale names.
          flushSync(() => {
            updateDrawingShapes((dShapes) =>
              dShapes.map((dShape) =>
                dShape.id === shape.id ? { ...dShape, label: text } : dShape
              )
            );
          });
          dispatch(updateNeedRedrawCanvas(true));
        });

      const renameSubscriptions = drawingTool
        .addShapeText(canvas, shapes, overlayColor)
        .map(shapeRenameListener);

      if (selectedEdge) {
        drawingTool.addEdgeName(
          canvas,
          selectedEdge.name,
          overlayColor,
          shapes
        );
      }

      return renameSubscriptions;
    },
    [canvas, dispatch, selectedEdge, updateDrawingShapes]
  );

  // on color change
  useEffect(() => {
    if (drawingShapes && selectedColor?.slabImageUrl && !!slabImg.url) {
      drawingTool.fillColor(
        canvas,
        selectedColor,
        drawingShapes,
        false,
        slabImg.element
      );
    }
  }, [slabImg.url, slabImg.element, canvas, selectedColor, drawingShapes]);

  // used to assign the selected edge to a shape of whom edge is undefined
  // basically this solve the issue of the first added shape in drawing area,
  // because there isn't any selected edge until the user add a shape in the drawing area
  useEffect(() => {
    drawingShapes.forEach((drawingShape) => {
      if (drawingShape.edge === undefined && selectedEdge !== undefined) {
        drawingShape.edge = selectedEdge;
      }
    });
  }, [drawingShapes, selectedEdge]);

  useEffect(() => {
    if (selectedColor) {
      drawingTool.selectShape({
        allShapes: drawingShapes,
        canvas,
        color: selectedColor.overlayColor,
        activeDrawingShape,
      });
    }
  }, [
    selectedColor,
    needRedrawCanvas,
    selectedEdge,
    drawingShapes,
    canvas,
    activeDrawingShape,
  ]);

  return {
    onDimensionEdit,
    updateShapesDimensions,
    updateShapesTexts,
  };
};
